/**
 * MindfulnessProgramTemplateCard - Card component for displaying a mindfulness program template
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { Theme } from '../../../Theme/Theme';
import { MindfulnessProgramTemplate } from '../../../Model/Coach/Mindfulness/Template/MindfulnessProgramTemplate';
import { getMediaLargeOrMediumOrSmallest } from '../../../Model/Media/Media';
import { getAthleteAllTags } from '../../../Model/Athlete/Athlete';

const Card = styled(Link)`
  display: block;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    text-decoration: none;
    color: inherit;
  }
`;

const ImageContainer = styled.div<{ $src?: string }>`
  position: relative;
  width: 100%;
  height: 180px;
  background: ${({ $src }) => ($src ? `url(${$src})` : 'linear-gradient(135deg, #8B5CF6, #7C3AED)')};
  background-size: cover;
  background-position: center;
`;

const ActiveBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: #8B5CF6;
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 20px;
`;

const TypeBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #8B5CF6;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 20px;
`;

const CardContent = styled.div`
  padding: 16px;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 0.85rem;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 12px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: ${Theme.Colors.charcoal};
`;

const MetaIcon = styled.span`
  font-size: 14px;
`;

export type MindfulnessProgramTemplateCardProps = {
  template: MindfulnessProgramTemplate;
  isActive?: boolean;
};

export const MindfulnessProgramTemplateCard: React.FC<MindfulnessProgramTemplateCardProps> = observer(
  ({ template, isActive }) => {
    const { t } = useTrans();
    const { authentication } = useRootStore();
    const title = useLocalized(template.name);
    const description = useLocalized(template.description);
    const image = template.getImage(authentication.athlete ? getAthleteAllTags(authentication.athlete) : []);
    const imageUrl = image ? getMediaLargeOrMediumOrSmallest(image) : undefined;

    const weeksCount = template.weeksCount;
    const daysPerWeek = template.minDaysPerWeek === template.maxDaysPerWeek
      ? `${template.minDaysPerWeek}`
      : `${template.minDaysPerWeek}-${template.maxDaysPerWeek}`;

    const typeLabel = template.type === 'meditation'
      ? t('mindfulnessPrograms.type.meditation', { defaultValue: 'Meditation' })
      : template.type === 'breathing'
      ? t('mindfulnessPrograms.type.breathing', { defaultValue: 'Breathing' })
      : t('mindfulnessPrograms.type.mindfulness', { defaultValue: 'Mindfulness' });

    return (
      <Card to={`/mental-health/mindfulness-programs/${template.id}`}>
        <ImageContainer $src={imageUrl}>
          <TypeBadge>{typeLabel}</TypeBadge>
          {isActive && (
            <ActiveBadge>
              {t('mindfulnessPrograms.badge.active', { defaultValue: 'Active' })}
            </ActiveBadge>
          )}
        </ImageContainer>
        <CardContent>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
          <MetaRow>
            <MetaItem>
              <MetaIcon>📅</MetaIcon>
              {weeksCount} {t('mindfulnessPrograms.meta.weeks', { defaultValue: 'weeks' })}
            </MetaItem>
            <MetaItem>
              <MetaIcon>🧘</MetaIcon>
              {daysPerWeek}x / {t('mindfulnessPrograms.meta.week', { defaultValue: 'week' })}
            </MetaItem>
            {template.minDurationInMinutes && (
              <MetaItem>
                <MetaIcon>⏱️</MetaIcon>
                {template.minDurationInMinutes === template.maxDurationInMinutes
                  ? `${template.minDurationInMinutes}`
                  : `${template.minDurationInMinutes}-${template.maxDurationInMinutes}`}{' '}
                {t('mindfulnessPrograms.meta.min', { defaultValue: 'min' })}
              </MetaItem>
            )}
          </MetaRow>
        </CardContent>
      </Card>
    );
  },
);
